import posthog from 'posthog-js'
import * as React from 'react'
import {
	data,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetcher,
	useLoaderData,
	useRevalidator,
	type ShouldRevalidateFunctionArgs,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { EarlyLinkActivation } from '#app/components/early-link-activation.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { ModalsOnQueryParams } from '#app/components/modals-on-query-params.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '#app/components/ui/dialog.tsx'
import { href as iconsHref, Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { UploadFileErrorDialog } from '#app/components/upload-file-error-dialog.tsx'
import { WelcomeWizardDialog } from '#app/components/welcome-wizard-dialog.tsx'
import {
	getRegisteredRequestLogContext,
	runWithLogContext,
	setAuthenticatedUserLogContext,
	setLogContextFields,
} from '#app/services/log-context.server.ts'
import { type Route } from './+types/root'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { EpicToaster } from './components/toaster.tsx'
import { SubscriptionPlans } from './services/billing/subscription-plans.ts'
import { getUserActiveSubscription, userHasActiveSubscription } from './services/billing/subscriptions.server.ts'
import { shouldSkipDocumentReviewSessionRevalidation } from './services/document-review/revalidation.ts'
import { OrganizationMemberRole } from './services/organizations/utils.ts'
import { toSidebarSectionIds } from './services/sidebar/sidebar-sections.ts'
import fontStyleSheetUrl from './styles/interfont.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { ACTIVE_APP_THEME } from './utils/app-themes.ts'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { registerNormalizedRequestUrl } from './utils/request-url.server.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const shouldRevalidate = (opts: ShouldRevalidateFunctionArgs) => {
	if (shouldSkipDocumentReviewSessionRevalidation(opts)) return false
	if (opts.formMethod === 'POST' || opts.formMethod === 'PUT' || opts.formMethod === 'PATCH') {
		return true
	}
	return false
}

export const middleware: Route.MiddlewareFunction[] = [
	async ({ request, url }, next) => {
		registerNormalizedRequestUrl(request, url)
		const logContext = getRegisteredRequestLogContext(request.headers.get('x-request-id'))
		if (!logContext) {
			return next()
		}
		return await runWithLogContext(logContext, async () => {
			return await next()
		})
	},
]

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload CSS as a resource to avoid render blocking
		{ rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
		{ rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: fontStyleSheetUrl },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
	let title = loaderData ? 'IUS PRO' : 'Error | IUS PRO'
	if (process.env.NODE_ENV === 'development') {
		title = 'IUS PRO (dev)'
	}
	return [
		{ title },
		{ name: 'description', content: 'Inteligencia Artificial para abogados en Costa Rica' },
		{ name: 'google', content: 'notranslate' },
		{ name: 'og:title', content: 'Ius Pro | Inteligencia Artificial para abogados en Costa Rica' },
		{
			name: 'og:description',
			content: 'Asistente Legal Especializado: Consultas Exhaustivas en Minutos y Análisis de Documentos',
		},
		{ name: 'og:image', content: 'https://iuspro.com/img/og-image.jpeg' },
	]
}

export async function loader({ request, url }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})
	setAuthenticatedUserLogContext(userId)

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							email: true,
							cachedAvailableAnalyses: true,
							cachedAvailableChats: true,
							cachedAvailableTranscriptions: true,
							enabledSidebarSections: true,
							orgnizationMembership: {
								select: {
									active: true,
									role: true,
									organizationId: true,
									organization: {
										select: { requiresMembersTwoFactor: true },
									},
								},
							},
							featureFlags: {
								select: {
									slug: true,
								},
							},
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const requiresTwoFactorSetup = user?.orgnizationMembership?.active
		? user.orgnizationMembership.organization.requiresMembersTwoFactor &&
			!(await prisma.verification.findUnique({
				where: { target_type: { target: user.id, type: '2fa' } },
				select: { id: true },
			}))
		: false
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()
	let hasActiveSubscription = userId ? Boolean(await userHasActiveSubscription({ userId })) : false
	if (user?.roles?.some(role => role.name === 'admin')) {
		hasActiveSubscription = true
	}
	const [activeSubscription, advancedPlan] =
		userId && !user?.orgnizationMembership?.organizationId
			? await Promise.all([
					getUserActiveSubscription({ userId }),
					prisma.subscriptionPlan.findFirst({
						where: { name: SubscriptionPlans.advanced!.title },
						select: {
							id: true,
							name: true,
							price: true,
							chatLimit: true,
							analysisLimit: true,
							transcriptionLimit: true,
						},
					}),
				])
			: [null, null]
	setLogContextFields({
		organization_id: user?.orgnizationMembership?.organizationId,
		feature_flags: user?.featureFlags.map(flag => flag.slug) ?? [],
		has_active_subscription: hasActiveSubscription,
		user_roles: user?.roles.map(role => role.name) ?? [],
	})

	return data(
		{
			user: {
				...user,
				hasActiveSubscription,
				activeSubscription: activeSubscription
					? {
							planId: activeSubscription.subscriptionPlanId,
							planName: activeSubscription.subscriptionPlan.name,
							price: activeSubscription.subscriptionPlan.price,
							chatLimit: activeSubscription.subscriptionPlan.chatLimit,
							analysisLimit: activeSubscription.subscriptionPlan.analysisLimit,
							transcriptionLimit: activeSubscription.subscriptionPlan.transcriptionLimit,
						}
					: null,
				upgradePlan: advancedPlan,
				isOrganizationOwner: user?.orgnizationMembership?.role === OrganizationMemberRole.Owner,
				canManageOrganizationAdministration: Boolean(
					user?.orgnizationMembership?.active &&
						[OrganizationMemberRole.Owner, OrganizationMemberRole.Admin].includes(user.orgnizationMembership.role),
				),
				isOrganizationMember: !!user?.orgnizationMembership?.organizationId,
				requiresTwoFactorSetup: Boolean(requiresTwoFactorSetup),
				enabledSidebarSections: toSidebarSectionIds(user?.enabledSidebarSections ?? []),
			},
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: url.pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders({ 'Server-Timing': timings.toString() }, toastHeaders),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
}) {
	theme = 'light'
	return (
		<html
			lang="es"
			className={`${theme} ${ACTIVE_APP_THEME.className} notranslate overflow-x-hidden`}
			suppressHydrationWarning
			translate="no"
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				{/* Browsers hide parsed nonce attributes from React; stylesheet links do not need the script CSP nonce. */}
				<Links nonce="" />
			</head>
			<body
				className="notranslate relative h-[100dvh] overflow-y-hidden bg-background text-foreground"
				translate="no"
				style={{ letterSpacing: '-0.2px' }}
			>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const theme = 'light'

	const runOnce = React.useRef(false)

	React.useEffect(() => {
		if (data.user && !runOnce.current) {
			posthog.identify(data.user.id, {
				username: data.user.username,
				email: data.user.email,
				name: data.user.name,
				organizationId: data.user.orgnizationMembership?.organizationId,
			})
			runOnce.current = true
		}
	}, [data])

	return (
		<Document nonce={nonce} theme={theme} env={data.ENV}>
			<EarlyLinkActivation />
			<Outlet />
			{data.user?.requiresTwoFactorSetup ? <RequiredTwoFactorDialog /> : null}
			{data.user ? <WelcomeWizardDialog isOrganizationMember={data.user.isOrganizationMember} /> : null}
			<UploadFileErrorDialog />
			<EpicToaster toast={data.toast} />
			<EpicProgress />
			<ModalsOnQueryParams />
		</Document>
	)
}

function RequiredTwoFactorDialog() {
	const [verified, setVerified] = React.useState(false)
	const hasLoadedSetup = React.useRef(false)
	const setupFetcher = useFetcher<{
		required: boolean
		enabled: boolean
		qrCode: string | null
		otpUri: string | null
	}>()
	const verifyFetcher = useFetcher<{
		status: 'idle' | 'success' | 'error'
		message?: string
		submission?: {
			error?: Record<string, string[]>
			payload?: Record<string, unknown>
		}
	}>()
	const revalidator = useRevalidator()

	React.useEffect(() => {
		if (hasLoadedSetup.current) return
		hasLoadedSetup.current = true
		void setupFetcher.load('/resources/required-two-factor')
	}, [setupFetcher])

	React.useEffect(() => {
		if (verifyFetcher.data?.status !== 'success' || verified) return
		setVerified(true)
		void revalidator.revalidate()
	}, [revalidator, verified, verifyFetcher.data?.status])

	const isSubmitting = verifyFetcher.state !== 'idle'
	const codeErrors = verifyFetcher.data?.submission?.error?.code

	if (verified || setupFetcher.data?.enabled) {
		return null
	}

	return (
		<Dialog open>
			<DialogContent
				className="max-h-[94dvh] overflow-y-auto p-0 sm:max-w-2xl sm:rounded-xl [&>button]:hidden"
				onEscapeKeyDown={event => event.preventDefault()}
				onPointerDownOutside={event => event.preventDefault()}
			>
				<div className="grid overflow-hidden sm:grid-cols-[0.95fr_1.05fr]">
					<div className="border-b border-neutral-200 bg-neutral-950 p-6 text-neutral-50 sm:border-b-0 sm:border-r">
						<div className="flex h-full min-h-[220px] flex-col justify-between gap-8">
							<DialogHeader className="space-y-3 text-left">
								<div className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10">
									<Icon name="shield" className="h-5 w-5 text-white" />
								</div>
								<div>
									<DialogTitle className="mb-2 text-xl font-semibold leading-tight tracking-normal text-white">
										Configura 2FA para continuar
									</DialogTitle>
									<DialogDescription className="text-sm leading-6 text-neutral-300">
										Tu organización requiere una capa adicional de seguridad antes de entrar a IUS PRO.
									</DialogDescription>
								</div>
							</DialogHeader>

							<div className="space-y-3 text-sm text-neutral-300">
								<div className="flex items-start gap-3">
									<span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-neutral-950">
										1
									</span>
									<span>Escanea el código QR con tu aplicación autenticadora.</span>
								</div>
								<div className="flex items-start gap-3">
									<span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-neutral-950">
										2
									</span>
									<span>Ingresa el código de 6 dígitos para activar el acceso.</span>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-[#fbfaf8] p-6">
						{setupFetcher.data?.qrCode ? (
							<div className="space-y-6">
								<div className="flex justify-center">
									<div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
										<img alt="Código QR para 2FA" src={setupFetcher.data.qrCode} className="h-48 w-48" />
									</div>
								</div>

								<verifyFetcher.Form method="POST" action="/resources/required-two-factor" className="space-y-4">
									<div className="space-y-2">
										<label htmlFor="required-two-factor-code" className="text-sm font-medium text-neutral-800">
											Código de verificación
										</label>
										<Input
											id="required-two-factor-code"
											name="code"
											inputMode="numeric"
											autoComplete="one-time-code"
											maxLength={6}
											placeholder="123456"
											className="h-12 text-center text-xl font-semibold tabular-nums tracking-[0.35em] placeholder:text-neutral-300"
											autoFocus
										/>
										<ErrorList errors={codeErrors} />
									</div>

									<StatusButton type="submit" className="h-11 w-full" status={isSubmitting ? 'pending' : 'idle'}>
										<Icon name="check" className="mr-2" />
										Verificar y continuar
									</StatusButton>
								</verifyFetcher.Form>

								<details className="group rounded-md border border-neutral-200 bg-white p-3">
									<summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-neutral-700">
										<span>Configurar manualmente</span>
										<Icon
											name="chevron-down"
											className="h-4 w-4 text-neutral-400 transition-transform group-open:rotate-180"
										/>
									</summary>
									<pre className="mt-3 whitespace-pre-wrap break-all rounded-md bg-neutral-50 p-3 font-mono text-xs leading-5 text-neutral-600">
										{setupFetcher.data.otpUri}
									</pre>
								</details>
							</div>
						) : (
							<div className="flex min-h-[320px] items-center justify-center">
								<Button disabled variant="ghost">
									<Icon name="update" className="mr-2 animate-spin" />
									Preparando código QR
								</Button>
							</div>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
